import { ComparatorContext } from '@kizi/comparator-core';
import { ButtonProps, Div, Heading, P, Span } from '@kizi/core-components';
import {
  Accordion,
  AccordionItem,
  IconV2 as Icon,
  IconV2Props as IconProps,
} from '@kizi/shared-components';
import React, { useContext, useRef, useState } from 'react';
import { Desktop } from '../../utils/breakpoint';
import { Window } from '../Window';
export interface ModalWithAccordionProps
  extends React.ComponentPropsWithRef<any> {
  buttonIcon?: string;
  buttonIconProps?: IconProps;
  buttonLabel: string;
  buttonProps?: ButtonProps;
  header?: string;
  items: AccordionItem[];
  title: string;
  contentType?: string;
  hideLabelOnMobile?: boolean;
}

interface ArrowCompProps extends Omit<IconProps, 'name'> {
  isOpen: boolean;
}

interface CustomAccordionProps {
  items: AccordionItem[];
  allowOneItemOpen?: boolean;
  startIndex?: number;
  iconColor?: string;
  selectedTabIndex?: number;
  size?: 'small';
  arrowLabels?: any[];
}

export const ArrowComp = ({
  isOpen,
  ...restProps
}: ArrowCompProps): JSX.Element => {
  const name = `big-arrow-${isOpen ? 'up' : 'down'}`;
  return <Icon name={name} height={16} width={16} {...restProps} />;
};

export const innerAccordionItemWithLabel =
  (
    label: React.ReactNode,
    index: number,
    icon?: string,
    headerSubtext?: string,
    iconColor?: string,
    size?: string,
    arrowLabels?: any[],
    footNote?: string,
  ) =>
  (isOpen: boolean) => {
    if (size === 'small') {
      return (
        <Div
          key={`label-${index}`}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          css={{ cursor: 'pointer' }}
        >
          <Div display="flex" alignItems="center">
            {icon && <Icon name={'green-check'} size={28} />}
            <Heading as="h4" variant="H4" my={[0, 2]} fontSize={[3, 4]}>
              {label}
            </Heading>
          </Div>
          <Div display="flex">
            {headerSubtext && <Span>{headerSubtext}</Span>}
            {arrowLabels && (
              <P color={'#428BF0'} variant="footnoteRegular" mr={2}>
                {isOpen ? arrowLabels[0] : arrowLabels[1]}
              </P>
            )}
            <ArrowComp isOpen={isOpen} fill={iconColor} />
          </Div>
        </Div>
      );
    }
    return (
      <Div>
        <Div
          key={`label-${index}`}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          css={{ cursor: 'pointer', gap: 10 }}
        >
          <Div display="flex" alignItems="center">
            {icon && <Icon name={'green-check'} size={28} />}
            <Heading as="h3" variant="H3" my={[0, 3]} fontSize={[3, 5]}>
              {label}
            </Heading>
          </Div>
          <Div display="flex">
            {headerSubtext && <Span>{headerSubtext}</Span>}
            <ArrowComp isOpen={isOpen} fill={iconColor} />
          </Div>
        </Div>

        {isOpen && footNote && (
          <P color={'#727888'} fontSize={'14px'} lineHeight={'24px'}>
            {footNote}
          </P>
        )}
      </Div>
    );
  };

export const CustomAccordion: React.FC<CustomAccordionProps> = ({
  items,
  allowOneItemOpen = false,
  startIndex = 0,
  iconColor,
  selectedTabIndex,
  size,
  arrowLabels,
}): JSX.Element => {
  const _items = items.map(
    (
      { label, content, icon, headerSubtext, footNote }: AccordionItem,
      index: number,
    ) => {
      let htmlContent;
      if (typeof content === 'string') {
        htmlContent = content;

        return {
          label: innerAccordionItemWithLabel(label, index, size),
          content: (
            <Div
              key={`content-${index}`}
              display="flex"
              flexDirection="column"
              mt={[3, 0]}
              variant="modalAccordionContent"
              dangerouslySetInnerHTML={htmlContent && { __html: htmlContent }}
            />
          ),
        };
      }

      return {
        label: innerAccordionItemWithLabel(
          label,
          index,
          icon,
          headerSubtext,
          iconColor,
          size,
          arrowLabels,
          footNote,
        ),
        content,
      };
    },
  );

  return (
    <Accordion
      items={_items}
      variant={
        size === 'small' ? 'accordionInnerModalSmall' : 'accordionInnerModal'
      }
      allowOneItemOpen={allowOneItemOpen}
      startIndex={startIndex}
      selectedTabIndex={selectedTabIndex || undefined}
    />
  );
};

export const ModalWithAccordion: React.FC<ModalWithAccordionProps> =
  React.forwardRef<HTMLDivElement, ModalWithAccordionProps>(
    (
      {
        buttonIcon,
        buttonIconProps,
        buttonLabel,
        buttonProps,
        header,
        items,
        title,
        variant,
        showIcon = true,
        hideLabelOnMobile = false,
      },
      ref,
    ) => {
      const { name } = useContext(ComparatorContext);
      const isOs = name.includes('os-');

      const [show, setShow] = useState(false);
      const buttonRef = useRef<HTMLButtonElement>(null);

      let content;

      if (header !== undefined) {
        content = (
          <>
            <P
              variant="footnoteRegular"
              py={[3, 6]}
              borderBottom="1px solid"
              borderBottomColor="divider"
              dangerouslySetInnerHTML={{ __html: header }}
            />
            <CustomAccordion items={items} />
          </>
        );
      } else {
        content = <CustomAccordion items={items} />;
      }

      return (
        <Div variant="filterSorting">
          <Span
            role="button"
            onClick={() => setShow(!show)}
            ref={buttonRef}
            display="flex"
            alignItems="center"
            variant={
              variant ? variant : isOs ? 'iconSortingTextItem' : 'iconTextItem'
            }
            style={{ cursor: 'pointer' }}
            {...buttonProps}
          >
            {buttonIcon && (
              <Icon
                name={buttonIcon}
                size={isOs ? 24 : '22px!important'}
                mr={2}
                variant="iconLink"
                {...buttonIconProps}
              />
            )}
            {hideLabelOnMobile ? (
              <Span
                display={['none', 'none', 'none', 'inline']}
                variant="footnoteRegular"
                color="textSecondary"
              >
                {buttonLabel}
              </Span>
            ) : (
              <Span
                display="inline"
                variant="footnoteRegular"
                color="textSecondary"
              >
                {buttonLabel}
              </Span>
            )}
          </Span>
          <Window
            showIcon={showIcon}
            onClose={() => setShow(false)}
            modalProps={{
              show: show,
              children: (
                <Div ref={ref}>
                  <Desktop>
                    <Heading
                      variant="H2"
                      as="h2"
                      color="heading"
                      mt={4}
                      mb={0}
                      textAlign="center"
                    >
                      {title}
                    </Heading>
                  </Desktop>
                  {content}
                </Div>
              ),
            }}
            show={show}
            title={title}
            type="modal"
          >
            {content}
          </Window>
        </Div>
      );
    },
  );
